'use strict';

define('vb/action/builtin/takePhotoAction',[
  'vb/private/utils',
  'vb/action/action',
  'ojs/ojmenu',
  'ojs/ojoption',
],
(Utils, Action) => {
  /** @typedef {import('vb/action/action').Outcome} Outcome */

  const MEDIA_TYPES = ['image', 'video', 'audio'];

  /**
   * Invokes the HtmlMediaCapture API to take pictures and/or choose images from the system's
   * image library.
   *
   * The parameters to this action are as follows:
   *  mediaType - Controls the type of media. Valid values are &lt;image&gt; and &quot;video&quot;
   *
   * Outcome from this action is file - a Blob that contains the &quot;image&quot;.
   *
   * @extends Action
   * @category Action
   */
  class TakePhotoAction extends Action {
    /**
     * Initiates the photo taking process using HTML Media Capture API.
     *
     * @param {Object} parameters - An object containing configuration options for the photo taking process.
     * @param {string} parameters.mediaType - The type of media to capture (e.g., "image" or "video").
     *
     * @returns {Promise<Outcome>} A promise that resolves with an Outcome object representing the result of the photo
     *                             taking process, which includes a `file` property containing the captured image
     *                             as a Blob. The promise may also reject with an error message if the operation fails.
     */
    // eslint-disable-next-line class-methods-use-this
    perform(parameters) {
      // Use Html media Capture
      // FYI: https://www.w3.org/TR/html-media-capture/
      return TakePhotoAction.performHtmlMediaCapture(parameters);
    }

    /**
     * Internal method to initiate image capture using HtmlMediaCapture.
     *
     * Creates an HTML input file element, sets its accept attribute based on the provided value,
     * and simulates a click event to trigger the file selection dialog.
     *
     * Resolves with a Promise containing an Outcome object when a file is selected,
     * or rejects with an error message if the operation fails.
     *
     * @private
     * @static
     * @param {string} accept - The accept attribute value for the file input element.
     * @returns {Promise<Outcome>} A Promise resolving with an Outcome object containing the selected file as a Blob.
     */
    static webPerform(accept) {
      return new Promise((resolve) => {
        // just invoke input element
        const fileElement = document.createElement('input');
        fileElement.type = 'file';
        fileElement.accept = accept;

        // For the file input element, onchange is not triggered if user cancels the file dialog. But the value is set
        // to null. The following onfocus() is a workaround to identify the cancel on file dialog.
        // In document.onfocus check if the file element value is null, if so the dialog was cancelled.
        // But there is some delay on setting the file element value by browsers. This 1000ms delay solves it.
        const bfocus = document.body.onfocus;
        document.body.onfocus = () => {
          setTimeout(() => {
            document.body.onfocus = bfocus; // restore document.body focus handler
            if (!fileElement.value) { // cancel clicked
              resolve(Action.createFailureOutcome('Take Photo Action cancelled'));
            }
          }, 1000);
        };

        fileElement.onchange = () => {
          document.body.onfocus = bfocus; // restore document.body focus handler
          // ServiceWorker thread cannot access File of Main thread. It needs to be converted into a Blob.
          const fileBlob = Utils.fileToBlob(fileElement.files[0]);
          resolve(Action.createSuccessOutcome({ file: fileBlob }));
        };

        fileElement.click();
      });
    }

    /**
     * Performs HTML media capture using the provided parameters.
     *
     * @private
     * @static
     * @param {Object} params - Parameters for the media capture process.
     * @param {string} params.mediaType - The type of media to capture (e.g., "image" or "video"). Defaults to "image"
     *                                    if not specified.
     *
     * @returns {Promise<Outcome>} A promise that resolves with an Outcome object containing the captured media as
     *                             a Blob, or rejects with an error message if the operation fails.
     */
    static performHtmlMediaCapture(params) {
      let mediaType = params.mediaType;
      if (!mediaType) {
        // WebApps or PWA did not have TakePhoto Action.
        // Either a MobileApp is getting executed in PWA mode or WebApp did not set mediaType.
        mediaType = 'image'; // Use default mediaType
      }
      mediaType = mediaType.toLowerCase();

      const accept = `${MEDIA_TYPES.includes(mediaType) ? mediaType : 'image'}/*`;

      return TakePhotoAction.webPerform(accept);
    }
  }

  return TakePhotoAction;
});

